import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

type ScreenKeys = 'mcpSettingsScreen' | 'mcpSettingsAddDomainModal' | 'mcpSettingsDeleteDomainModal' | 'mcpSettingsEditPermissionModal';
type UIEventKeys =
  | 'mcpSettingsAddDomainSubmit'
  | 'mcpSettingsDeleteDomainConfirm'
  | 'mcpSettingsTabClicked'
  | 'mcpSettingsEditPermissionClicked'
  | 'mcpSettingsEditPermissionCancelled'
  | 'mcpSettingsPermissionToggled';
type TrackEventKeys =
  | 'mcpSettingsToggleAtlassianDomainsSuccess'
  | 'mcpSettingsToggleAtlassianDomainsFailure'
  | 'mcpSettingsAddDomainSuccess'
  | 'mcpSettingsAddDomainFailure'
  | 'mcpSettingsDeleteDomainSuccess'
  | 'mcpSettingsDeleteDomainFailure'
  | 'mcpSettingsPermissionUpdatedSuccess'
  | 'mcpSettingsPermissionUpdatedFailure'
  | 'mcpSettingsPermissionToggleSuccess'
  | 'mcpSettingsPermissionToggleFailure';

export enum McpSettingsScreens {
  mcpSettingsScreen = 'mcpSettingsScreen',
  mcpSettingsAddDomainModal = 'mcpSettingsAddDomainModal',
  mcpSettingsDeleteDomainModal = 'mcpSettingsDeleteDomainModal',
  mcpSettingsEditPermissionModal = 'mcpSettingsEditPermissionModal',
}

const screen = {
  // https://data-portal.internal.atlassian.com/analytics/registry/91148
  mcpSettingsScreen: (): ScreenEvent => ({
    data: {
      name: McpSettingsScreens.mcpSettingsScreen,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91148',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/91291
  mcpSettingsAddDomainModal: (): ScreenEvent => ({
    data: {
      name: McpSettingsScreens.mcpSettingsAddDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91291',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/91292
  mcpSettingsDeleteDomainModal: (): ScreenEvent => ({
    data: {
      name: McpSettingsScreens.mcpSettingsDeleteDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91292',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/99717
  // Fired when the edit permission modal is opened for a specific permission kind (read, write, search).
  // Attributes: permissionKind - the permission being edited ('read' | 'write' | 'search')
  mcpSettingsEditPermissionModal: (attributes?: { permissionKind: string }): ScreenEvent => ({
    data: {
      name: McpSettingsScreens.mcpSettingsEditPermissionModal,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99717',
  }),
};

const ui = {
  // https://data-portal.internal.atlassian.com/analytics/registry/91294
  mcpSettingsAddDomainSubmit: (): UIEvent => ({
    data: {
      action: Action.Clicked,
      actionSubject: ActionSubject.Button,
      actionSubjectId: 'mcpSettingsAddDomainSubmit',
      source: McpSettingsScreens.mcpSettingsAddDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91294',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/91295
  mcpSettingsDeleteDomainConfirm: (): UIEvent => ({
    data: {
      action: Action.Clicked,
      actionSubject: ActionSubject.Button,
      actionSubjectId: 'mcpSettingsDeleteDomainConfirm',
      source: McpSettingsScreens.mcpSettingsDeleteDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91295',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/99718
  // Fired when the user switches between the Domains and Permissions tabs on the MCP Settings page.
  // Attributes: tabName - the tab the user switched to ('domains' | 'permissions')
  mcpSettingsTabClicked: (attributes?: { tabName: string }): UIEvent => ({
    data: {
      action: Action.Clicked,
      actionSubject: ActionSubject.Tab,
      actionSubjectId: 'mcpSettingsTabClicked',
      source: McpSettingsScreens.mcpSettingsScreen,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99718',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/99719
  // Fired when the user clicks the "Edit" button on a permission row in the permissions table.
  // Attributes: permissionKind - the permission row that was clicked ('read' | 'write' | 'search')
  mcpSettingsEditPermissionClicked: (attributes?: { permissionKind: string }): UIEvent => ({
    data: {
      action: Action.Clicked,
      actionSubject: ActionSubject.Button,
      actionSubjectId: 'mcpSettingsEditPermissionClicked',
      source: McpSettingsScreens.mcpSettingsScreen,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99719',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/99720
  // Fired when the user clicks the "Cancel" button in the edit permission modal without saving.
  // Attributes: permissionKind - the permission that was being edited ('read' | 'write' | 'search')
  mcpSettingsEditPermissionCancelled: (attributes?: { permissionKind: string }): UIEvent => ({
    data: {
      action: Action.Clicked,
      actionSubject: ActionSubject.Button,
      actionSubjectId: 'mcpSettingsEditPermissionCancelled',
      source: McpSettingsScreens.mcpSettingsEditPermissionModal,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99720',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/99721
  // Fired when the user clicks the toggle checkbox on a permission row in the permissions table.
  // Attributes: permissionKind - the permission being toggled ('read' | 'write' | 'search'), enabled - the new state after toggle
  mcpSettingsPermissionToggled: (attributes?: { permissionKind: string; enabled: boolean }): UIEvent => ({
    data: {
      action: Action.Clicked,
      actionSubject: ActionSubject.Toggle,
      actionSubjectId: 'mcpSettingsPermissionToggled',
      source: McpSettingsScreens.mcpSettingsScreen,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99721',
  }),
};

const track = {
  // Toggle Operations
  // https://data-portal.internal.atlassian.com/analytics/registry/91300
  mcpSettingsToggleAtlassianDomainsSuccess: (attributes: { enabled: boolean }): TrackEvent => ({
    data: {
      action: TrackAction.Toggled,
      actionSubject: TrackActionSubject.AtlassianSupportedMCPDomains,
      actionSubjectId: 'mcpSettingsToggleAtlassianDomainsSuccess',
      source: McpSettingsScreens.mcpSettingsScreen,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91300',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/91312
  mcpSettingsToggleAtlassianDomainsFailure: (): TrackEvent => ({
    data: {
      action: TrackAction.ChangeFailed,
      actionSubject: TrackActionSubject.AtlassianSupportedMCPDomains,
      actionSubjectId: 'mcpSettingsToggleAtlassianDomainsFailure',
      source: McpSettingsScreens.mcpSettingsScreen,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91312',
  }),

  // Add Domain Operations
  // https://data-portal.internal.atlassian.com/analytics/registry/91296
  mcpSettingsAddDomainSuccess: (attributes?: { domainPattern?: string }): TrackEvent => ({
    data: {
      action: TrackAction.Added,
      actionSubject: TrackActionSubject.MCPDomain,
      actionSubjectId: 'mcpSettingsAddDomainSuccess',
      source: McpSettingsScreens.mcpSettingsAddDomainModal,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91296',
  }),

  // https://data-portal.internal.atlassian.com/analytics/registry/91299
  mcpSettingsAddDomainFailure: (): TrackEvent => ({
    data: {
      action: TrackAction.CreationFailed,
      actionSubject: TrackActionSubject.MCPDomain,
      actionSubjectId: 'mcpSettingsAddDomainFailure',
      source: McpSettingsScreens.mcpSettingsAddDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91299',
  }),

  // Delete Domain Operations
  // https://data-portal.internal.atlassian.com/analytics/registry/91298
  mcpSettingsDeleteDomainSuccess: (): TrackEvent => ({
    data: {
      action: TrackAction.Deleted,
      actionSubject: TrackActionSubject.MCPDomain,
      actionSubjectId: 'mcpSettingsDeleteDomainSuccess',
      source: McpSettingsScreens.mcpSettingsDeleteDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91298',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/91313
  mcpSettingsDeleteDomainFailure: (): TrackEvent => ({
    data: {
      action: TrackAction.ChangeFailed,
      actionSubject: TrackActionSubject.MCPDomain,
      actionSubjectId: 'mcpSettingsDeleteDomainFailure',
      source: McpSettingsScreens.mcpSettingsDeleteDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91313',
  }),

  // Permission Toggle Operations
  // https://data-portal.internal.atlassian.com/analytics/registry/99722
  // Fired when a permission row toggle API call completes successfully.
  // Attributes: permissionKind - the permission that was toggled ('read' | 'write' | 'search'), enabled - the new enabled state
  mcpSettingsPermissionToggleSuccess: (attributes?: { permissionKind: string; enabled: boolean }): TrackEvent => ({
    data: {
      action: TrackAction.Toggled,
      actionSubject: TrackActionSubject.MCPPermission,
      actionSubjectId: 'mcpSettingsPermissionToggleSuccess',
      source: McpSettingsScreens.mcpSettingsScreen,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99722',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/99723
  // Fired when a permission row toggle API call fails.
  // Attributes: permissionKind - the permission that failed to toggle ('read' | 'write' | 'search')
  mcpSettingsPermissionToggleFailure: (attributes?: { permissionKind: string }): TrackEvent => ({
    data: {
      action: TrackAction.ChangeFailed,
      actionSubject: TrackActionSubject.MCPPermission,
      actionSubjectId: 'mcpSettingsPermissionToggleFailure',
      source: McpSettingsScreens.mcpSettingsScreen,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99723',
  }),

  // Edit Permission Modal Operations
  // https://data-portal.internal.atlassian.com/analytics/registry/99724
  // Fired when the user saves changes in the edit permission modal successfully.
  // Attributes: permissionKind - the permission that was updated ('read' | 'write' | 'search'),
  //             toolsets - the list of toolset IDs that were saved, applyToFuture - whether "allow all future toolsets" was enabled
  mcpSettingsPermissionUpdatedSuccess: (attributes?: {
    permissionKind: string;
    toolsets: string[];
    applyToFuture: boolean;
  }): TrackEvent => ({
    data: {
      action: TrackAction.Updated,
      actionSubject: TrackActionSubject.MCPPermission,
      actionSubjectId: 'mcpSettingsPermissionUpdatedSuccess',
      source: McpSettingsScreens.mcpSettingsEditPermissionModal,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99724',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/99725
  // Fired when the user's save action in the edit permission modal fails due to an API error.
  // Attributes: permissionKind - the permission that failed to update ('read' | 'write' | 'search')
  mcpSettingsPermissionUpdatedFailure: (attributes?: { permissionKind: string }): TrackEvent => ({
    data: {
      action: TrackAction.EditFailed,
      actionSubject: TrackActionSubject.MCPPermission,
      actionSubjectId: 'mcpSettingsPermissionUpdatedFailure',
      source: McpSettingsScreens.mcpSettingsEditPermissionModal,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99725',
  }),
};

export const mcpSettingsEvents: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, string> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/mcp/pages/5978033203/MCP+Settings+AdminHub+-+Analytics+Spec',
  screen,
  ui,
  track,
  operational: {},
};
